import { useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDue } from "@/lib/subjects";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const PhotoHomework = ({ onAdded }) => {
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  const [pick, setPick] = useState({});
  const read = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("files", file);
    try {
      const { data } = await api.post("/homework/photo", fd);
      const list = Array.isArray(data?.items) ? data.items : [];
      if (!list.length) toast.error("Couldn't spot any homework in that photo");
      else { setItems(list); setPick(Object.fromEntries(list.map((_, i) => [i, true]))); }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const add = async () => {
    const chosen = (items ?? []).filter((_, i) => pick[i]);
    try { for (const it of chosen) await api.post("/homework", it); toast.success(`Added ${chosen.length} item${chosen.length === 1 ? "" : "s"}`); setItems(null); onAdded?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <label data-testid="photo-homework-button" title="Add from a photo" className="grid place-items-center w-11 h-11 shrink-0 rounded-2xl border border-slate-300 text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
        <input data-testid="photo-homework-input" type="file" accept="image/*" capture="environment" className="hidden" disabled={busy} onChange={(e) => { read(e.target.files?.[0]); e.target.value = ""; }} />
      </label>
      <Dialog open={!!items} onOpenChange={(o) => !o && setItems(null)}>
        <DialogContent data-testid="photo-homework-dialog" className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-3xl font-normal">Found in your photo</DialogTitle></DialogHeader>
          <ul className="space-y-2">
            {(items ?? []).map((it, i) => (
              <li key={i}>
                <button data-testid={`photo-item-${i}`} onClick={() => setPick({ ...pick, [i]: !pick[i] })} className="w-full flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-left">
                  <span className={`mt-0.5 grid place-items-center w-5 h-5 shrink-0 rounded-md border-2 ${pick[i] ? "bg-brand border-brand text-white" : "border-slate-400"}`}>{pick[i] && <Check className="w-3.5 h-3.5" />}</span>
                  <span className="min-w-0"><span className="block font-semibold text-slate-900">{it.kind === "test" ? "Test: " : ""}{it.title}</span><span className="block text-xs text-slate-500">{it.subject || "No subject"} · {it.kind === "test" ? "on" : "due"} {fmtDue(it.due)}</span></span>
                </button>
              </li>
            ))}
          </ul>
          <button data-testid="photo-homework-add" onClick={add} disabled={!Object.values(pick).some(Boolean)} className="w-full rounded-xl bg-brand px-5 py-3 font-semibold text-white hover:bg-brand-dark disabled:opacity-40">Add selected</button>
        </DialogContent>
      </Dialog>
    </>
  );
};
